import { useCallback } from 'react';
import uniqBy from 'lodash-es/uniqBy';
import { useIntegrationsProvider } from 'shared/providers/IntegrationsProvider';
import {
  ClinicPimsIntegrationType,
  GetExportabilityCheckChannelQuery,
  GetExportabilityCheckChannelQueryVariables,
  GetExportabilityCheckChannelDocument,
  ClinicPet,
} from 'shared/types/graphql';
import { STATUSES } from 'shared/hooks/useCustomToast';
import { useApolloClient } from '@apollo/client';
import useClinicUser from 'shared/hooks/useClinicUser';

export type CheckChannelExportabilityResponse =
  | undefined
  | {
      message: string;
      status: STATUSES;
      time: number;
    };

const useCheckChannelExportability = (): {
  checkChannelExportability: (channelId: string) => Promise<CheckChannelExportabilityResponse>;
} => {
  const { currentClinicId } = useClinicUser();
  const { primaryIntegration, primaryIntegrationName } = useIntegrationsProvider();
  const apolloClient = useApolloClient();

  const getExportabilityCheckChannel = useCallback(
    async (channelId: string) => {
      // TODO: Once we upgrade Apollo Client to >= 3.5.0, consider using a lazy query here with fetchPolicy: 'cache-and-network'
      const response = await apolloClient.query<
        GetExportabilityCheckChannelQuery,
        GetExportabilityCheckChannelQueryVariables
      >({
        query: GetExportabilityCheckChannelDocument,
        variables: { channelId, clinicId: currentClinicId },
        fetchPolicy: 'network-only',
      });
      return response.data.findUniqueChannel;
    },
    [apolloClient, currentClinicId],
  );

  /**
   * Only require the following for exports:
   *  1. All integrations: At least one pet with a pimsId
   *  2. Datapoint: At least one client with a pimsId
   */
  const checkChannelExportability = useCallback(
    async (channelId: string) => {
      /**
       * Current backend export logic from pims-api (channelExportTemplate):
       *
       *  For pets:
       *    Get the pets, first based off association with the channel
       *    Second, based off the latest appointment linked to the channel
       *    Third, based off of the ones the clinicPetParent has
       *
       *  For pet parents:
       *    Get all pet parents, regardless of whether they've been removed from the channel
       */

      let isExportable = false;

      // TODO (future PR): Match backend logic by looking for pets through the latest channel-linked appointment
      const channel = await getExportabilityCheckChannel(channelId);

      if (!channel) {
        return undefined;
      }

      let pets: Pick<ClinicPet, 'id' | 'pimsId'>[] = [];

      if (channel.pets?.length) {
        pets = channel.pets;
      } else if (channel.channelMembers?.length) {
        pets = uniqBy(
          channel.channelMembers.flatMap(({ clinicPetParent }) => clinicPetParent?.pets || []),
          'id',
        );
      }

      isExportable = pets.some((pet) => !!pet.pimsId);

      if (!isExportable) {
        return {
          message: `This conversation can't be exported because no linked patient is associated with ${primaryIntegrationName}. Please link a patient from ${primaryIntegrationName} to this conversation and try again.`,
          status: STATUSES.warning,
          time: 5000,
        };
      }

      if (isExportable && primaryIntegration?.type === ClinicPimsIntegrationType.Datapoint) {
        const pimsPetParents =
          channel.channelMembers?.flatMap(({ clinicPetParent }) => clinicPetParent?.pimsId || []) || [];
        isExportable = !!pimsPetParents.length;
        if (!pimsPetParents.length) {
          return {
            message: `This conversation can't be exported because no linked client is associated with ${primaryIntegrationName}. Please add a client from ${primaryIntegrationName} to this conversation and try again.`,
            status: STATUSES.warning,
            time: 5000,
          };
        }
      }
    },
    [primaryIntegration?.type, primaryIntegrationName, getExportabilityCheckChannel],
  );

  return {
    checkChannelExportability,
  };
};

export default useCheckChannelExportability;
